<script lang="ts">
    import Chart from "chart.js";
    import {
        format,
        isBefore,
        eachDayOfInterval,
        subDays,
        isWeekend,
        isSameDay,
        addHours,
    } from "date-fns";
    import { 
        getTimezoneOffset, 
        utcToZonedTime, 
        zonedTimeToUtc 
    } from "date-fns-tz";
    import type { RGB } from "@cohesionib/biblioteca/lib/HexTheme";

    import { getAppContext } from "../../AppContext";
    const { feGatewaySdk, buildingCodeDemoList, buildingCode } = getAppContext();
    import type { GetTotalOccupantCountByBucketQuery } from "../../frontendGatewaySdk";
    import { hexToRGB } from "@cohesionib/biblioteca/lib/HexTheme";

    export let pickerStartDate: Date = new Date();
    export let pickerEndDate: Date = new Date();
    export let includeWeekends: boolean = false;

    let totalOccupantCountByBucketData: GetTotalOccupantCountByBucketQuery | null = null;
    let error: string | null = null;
    let isLoading: boolean = false;

    const rgbToRGBA = (color: RGB, opacity: number): string =>
        `rgba(${color.r}, ${color.g}, ${color.b}, ${opacity})`;
    const labels = [
        "6am",
        "7am",
        "8am",
        "9am",
        "10am",
        "11am",
        "12pm",
        "1pm",
        "2pm",
        "3pm",
        "4pm",
        "5pm",
        "6pm",
    ];
    const colors = {
        "Monday": "#1D55B2",
        "Tuesday":"#041A38",
        "Wednesday": "#367589",
        "Thursday": "#89C6AE",
        "Friday": "#AE425F",
        "Saturday": "#218265",
        "Sunday": "#F5B070",
    }
    const chicagoTimeZone = "America/Chicago";
    const localTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

    const formatDates = (
        pickerStartDate: Date, 
        pickerEndDate: Date, 
        includeWeekends: boolean
    ): Array<Date> => {
        const days = eachDayOfInterval({ 
            start: pickerStartDate,
            end: subDays(pickerEndDate, 1)
        });
        return days.reduce((arrayOfDays: Array<Date>, day) => {
            const timeZone = 
                getTimezoneOffset(localTimeZone) < getTimezoneOffset(chicagoTimeZone) 
                ? localTimeZone
                : chicagoTimeZone;

            if(!isWeekend(day)) {
                arrayOfDays.push(zonedTimeToUtc(day, timeZone))
            } else {
                if(includeWeekends) {
                    arrayOfDays.push(zonedTimeToUtc(day, timeZone))
                }
            }
            return arrayOfDays
        }, []);
    }
    $: dates = formatDates(
        pickerStartDate, 
        pickerEndDate, 
        includeWeekends
    )
    $: totalOccupantCountByBucketPromise = feGatewaySdk
        .getTotalOccupantCountByBucket({
            buildingCode: buildingCodeDemoList.includes(buildingCode.toLowerCase()) ? "wwsf1" : buildingCode.toLowerCase(),
            dates,
        })
        .then((d: any) => {
            error = null;
            totalOccupantCountByBucketData = d;
            isLoading = false;
        })
        .catch((e: string) => {
            error = "An error occurred. Please try again using another selected date(s)";
            isLoading = false;
        });
    $: totalOccupantCountByBucket = totalOccupantCountByBucketData?.building.totalOccupantCountByBucket || [];

    const getValues = (buckets: Array<Array<number>>) => {
        const total = buckets.reduce((a, b) => a.map((c, i) => (c + b[i])));
        return total.map((value) => Math.round(value / buckets. length))
    }

    let chart = {};
    const chartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        legend: {
            display: true,
            position: "top",
            labels: {
                usePointStyle: true,
            },
        },
        animation: {
            duration: 600,
        },
        scales: {
            yAxes: [
                {
                    ticks: {
                        suggestedMin: 0,
                    },
                },
            ],
        },
    };

    const initGraph = (element: HTMLCanvasElement) => {
        chart = new Chart(element, {
            type: "line",
            options: chartOptions,
            data: {
                labels,
                datasets: [],
            },
        });
    };

    const getDataSets = () => {
        const bucketByDays = dates.reduce((arrayOfDays, day) => {
            const name = format(new Date(day), "EEEE");
            const arrayOfDaysDate = new Date(day);
            const bucket = totalOccupantCountByBucket.filter((occupant) => {
                const bucketDate = new Date(occupant.bucket);
                return isSameDay(
                    utcToZonedTime(bucketDate, chicagoTimeZone), 
                    utcToZonedTime(arrayOfDaysDate, chicagoTimeZone)
                )
            });
            const filteredBucket = bucket.map((d) => ({
                x: utcToZonedTime(d?.bucket || "", chicagoTimeZone),
                y: Math.max(d?.count || 0, 0),
            }))
            .map((d) => ({
                /*
                    Because the graph's x-axis represents bucket end time 
                    and the back-end data timestamp represents bucket start time,
                    add an hour to back-end timestamp to sync front-end and back-end.
                */
                x: addHours(d.x, 1),
                y: d.y,
            }))
            .filter((d) => 
                d.x.getHours() >= parseInt(labels[0]?.match(/(\d+)/)[0]) &&
                isBefore(d.x, utcToZonedTime(Date.now(), chicagoTimeZone))
            )
            .map((d) => d.y);

            !arrayOfDays[name] ? arrayOfDays[name] = [[...filteredBucket]] : arrayOfDays[name].push(filteredBucket);
            return arrayOfDays
        }, {});

        return Object.keys(bucketByDays).map(function(key) {
            return  {
                data: getValues(bucketByDays[key]),
                label: `${key} Avg`,
                pointBackgroundColor: colors[key],
                borderColor: rgbToRGBA(hexToRGB(colors[key]), 0.9),
                backgroundColor: "rgba(0,0,0,0)",
            }
        });
    }

    const updateOnValuesChange = (element: HTMLCanvasElement) => {
        return {
            update() {
                if (chart) {
                    chart.destroy();
                }
                chart = null;
                chart = new Chart(element, {
                    type: "line",
                    options: chartOptions,
                    data: {
                        labels,
                        datasets: getDataSets(),
                    },
                });
            },
        };
    };
</script>

<style>
    #chart-container {
        width: 100%;
        display: flex;
        justify-content: center;
        align-items: center;
        height: 21em;
        padding-top: 1rem;
    }
    #chart-wrapper {
        height: 100%;
        max-width: 98rem;
        min-width: 20rem;
        width: calc(100% - 4rem);
        position: relative;
    }
</style>

<div id="chart-container">
    <div id="chart-wrapper"><canvas id="line-graph" use:initGraph use:updateOnValuesChange={totalOccupantCountByBucketData}/></div>
</div>
